import type { Ref } from 'vue'

import { useCustomerStore } from '@entities/customer'

import { DisciplineGeneralModal } from './enums'
import { useDisciplineStore } from './store'
import { useDisciplineGeneralInfoStore } from './general-info/store'
import { useDisciplineMergeProfilesApi } from '../api/merge-profiles.hook'
import { useDisciplineRefresh } from './discipline-refresh.hook'

interface DisciplineModalMergeConfirmReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refTitle: ComputedRef<string>
  refDetail: ComputedRef<string>
  goBack: () => void
  confirmMerge: () => Promise<void>
}

export const useDisciplineModalMergeConfirm = (): DisciplineModalMergeConfirmReturn => {
  const disciplineStore = useDisciplineStore()
  const generalInfoStore = useDisciplineGeneralInfoStore()
  const customerStore = useCustomerStore()
  const { refreshDiscipline } = useDisciplineRefresh()

  const refIsLoading = ref(false)

  const refIsOpen = computed<boolean>(() => generalInfoStore.getActiveModal === DisciplineGeneralModal.MergeConfirm)

  const refKidName = computed<string>(() => disciplineStore.getDetails?.kid.name ?? '')

  const refTitle = computed<string>(() => `Merge copies into ${refKidName.value}`)

  const refDetail = computed<string>(() => {
    const details = disciplineStore.getDetails
    const selectedIds = generalInfoStore.getMergeIds
    const kid = customerStore.getCustomer?.kids.find(item => item.id === details?.kid_id)
    const codes = (kid?.disciplines ?? [])
      .filter(discipline => selectedIds.includes(discipline.id))
      .map(discipline => discipline.code)
      .join(', ')

    return `Are you sure you want to merge ${codes} to ${refKidName.value} (${details?.code ?? ''})? This action cannot be undone.`
  })

  const goBack = (): void => {
    generalInfoStore.setActiveModal(DisciplineGeneralModal.Merge)
  }

  /** A refused merge reopens the picker, so the admin can drop the discipline the error names. */
  const confirmMerge = async (): Promise<void> => {
    const details = disciplineStore.getDetails
    if (!details || refIsLoading.value) return

    const survivor = await useDisciplineMergeProfilesApi({
      disciplineId: details.id,
      disciplines: generalInfoStore.getMergeIds,
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    if (!survivor) {
      goBack()
      return
    }

    generalInfoStore.setActiveModal(null)
    generalInfoStore.setMergeIds([])
    await refreshDiscipline({ withSiblings: true })
  }

  return {
    refIsOpen,
    refIsLoading,
    refTitle,
    refDetail,
    goBack,
    confirmMerge,
  }
}
